import React, { useState, useEffect, useCallback } from 'react';
import ServiceNavigation from '../ServiceNavigation.jsx';
import ExtendingPanel from '../ExtendingPanel.jsx';
import BrowserCheck from '../BrowserCheck';
import TopPanel from '../TopPanel';
import SynopsisPanelV3 from '../SynopsisPanelV3';
import { sendAnalytics, sendError } from '../../resources/rum-provider';
import { IMAGE_SEARCH_ENDPOINT } from '../../resources/prod-env.jsx';

import {
    AppLayout,
    SpaceBetween,
    Container,
    Input,
    Select,
    Button,
    Form,
    FormField,
    Alert,
    StatusIndicator,
    Cards,
    Badge,
    Box,
    Link,
    ColumnLayout,
    Modal,
} from '@cloudscape-design/components';

import '../../styles/form.scss';

const demoModule = "ImageSearch";

// LOCAL (npm start): prod-env is empty, so fall back to the CRA proxy path
// (/api/imagesearch → http://localhost:8084, see setupProxy.js).
// DEPLOYED: prod-env carries the real CloudFront URL, filled in by deploy.sh.
const SEARCH_URL = IMAGE_SEARCH_ENDPOINT
    ? `${IMAGE_SEARCH_ENDPOINT.replace(/\/$/, '')}/search`
    : '/api/imagesearch/search';

// Mirrors the `kind` enum the blog-checker vision model classifies images
// into (see backend/blog-checker/agents/prompts.py, IMAGE_REVIEWER_PROMPT).
const KIND_OPTIONS = [
    { label: 'Any kind',             value: '' },
    { label: 'Console screenshot',   value: 'console_screenshot' },
    { label: 'Architecture diagram', value: 'architecture_diagram' },
    { label: 'Sequence diagram',     value: 'sequence_diagram' },
    { label: 'Network diagram',      value: 'network_diagram' },
    { label: 'Flow diagram',         value: 'flow_diagram' },
    { label: 'Chart',                value: 'chart' },
    { label: 'Code screenshot',      value: 'code_screenshot' },
    { label: 'Photo',                value: 'photo' },
    { label: 'Other',                value: 'other' },
];

const KIND_BADGE_COLOR = {
    console_screenshot:   'blue',
    architecture_diagram: 'green',
    sequence_diagram:     'green',
    network_diagram:      'green',
    flow_diagram:         'green',
    chart:                'grey',
    code_screenshot:      'blue',
    photo:                'grey',
    other:                'grey',
};

const KIND_LABEL = KIND_OPTIONS.reduce((acc, o) => {
    if (o.value) acc[o.value] = o.label;
    return acc;
}, {});

// Mirrors executor.classify_source() in backend/blog-checker/executor.py —
// a cheap URL-only classification of what kind of AWS content the image's
// source page is, denormalized onto every indexed image record.
const SOURCE_KIND_OPTIONS = [
    { label: 'Any source',   value: '' },
    { label: 'Blog',         value: 'blog' },
    { label: 'Whitepaper',   value: 'whitepaper' },
    { label: 'Workshop',     value: 'workshop' },
    { label: 'Video',        value: 'video' },
    { label: 'Repost',       value: 'repost' },
    { label: 'Builder',      value: 'builder' },
    { label: 'Docs',         value: 'docs' },
];

const SOURCE_KIND_LABEL = SOURCE_KIND_OPTIONS.reduce((acc, o) => {
    if (o.value) acc[o.value] = o.label;
    return acc;
}, {});

// Images extracted from a PDF (whitepapers) carry a synthetic
// `pdf:<url>#page=N&img=M` src rather than a real fetchable image URL — see
// backend/blog-checker/image_index.py: no image bytes are ever stored, only
// metadata, so there is nothing to point a thumbnail at for these. Detect
// that shape and render a placeholder instead of a broken <img>.
function isPdfSrc(src) {
    return typeof src === 'string' && src.startsWith('pdf:');
}

function pdfPageNumber(src) {
    const m = /[#&]page=(\d+)/.exec(src || '');
    return m ? m[1] : null;
}

// `onZoom`, if provided, is wired onto the real <img> only — the PDF/
// broken-image placeholder has nothing to zoom into, so it stays
// non-interactive.
function Thumbnail({ result, onZoom }) {
    const [failed, setFailed] = useState(false);
    const pdf = isPdfSrc(result.src);

    if (pdf || failed) {
        return (
            <Box
                textAlign="center"
                color="text-body-secondary"
                padding="l"
                style={{
                    width: '100%', aspectRatio: '4/3', display: 'flex',
                    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--color-background-container-content-awsui-tzqwkz, #f2f3f3)',
                    borderRadius: 6,
                }}
            >
                <Box fontSize="display-l">📄</Box>
                <Box fontSize="body-s">
                    {pdf ? `PDF — page ${pdfPageNumber(result.src) || '?'}` : 'Image unavailable'}
                </Box>
            </Box>
        );
    }

    return (
        <img
            src={result.src}
            alt={result.alt || result.description || ''}
            onError={() => setFailed(true)}
            onClick={() => onZoom?.(result)}
            onKeyDown={(e) => {
                if (onZoom && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onZoom(result);
                }
            }}
            role={onZoom ? 'button' : undefined}
            tabIndex={onZoom ? 0 : undefined}
            aria-label={onZoom ? 'Click to enlarge image' : undefined}
            title={onZoom ? 'Click to enlarge' : undefined}
            style={{
                width: '100%', aspectRatio: '4/3', objectFit: 'contain', borderRadius: 6,
                background: '#f2f3f3', cursor: onZoom ? 'zoom-in' : undefined,
            }}
        />
    );
}

export default function ImageSearch(props) {
    const [query, setQuery]       = useState('');
    const [kind, setKind]         = useState(KIND_OPTIONS[0]);
    const [sourceKind, setSourceKind] = useState(SOURCE_KIND_OPTIONS[0]);
    const [results, setResults]   = useState([]);
    const [searched, setSearched] = useState(false);
    const [status, setStatus]     = useState('info');
    const [statusText, setStatusText] = useState("Enter a query and hit 'Search'");
    const [alertVisible, setAlertVisible] = useState(false);
    const [zoomedResult, setZoomedResult] = useState(null);

    useEffect(() => { sendAnalytics({ demo: demoModule }); }, []);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        const q = query.trim();
        if (!q) {
            setAlertVisible(true);
            return;
        }
        setAlertVisible(false);
        setStatus('loading');
        setStatusText('Searching…');
        sendAnalytics({
            demo: demoModule, action: 'search', query: q,
            kind: kind.value || 'any', sourceKind: sourceKind.value || 'any',
        });

        fetch(SEARCH_URL, {
            method: 'POST',
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                query: q,
                top_k: 12,
                kind: kind.value || null,
                source_kind: sourceKind.value || null,
            }),
        })
            .then(r => r.json())
            .then(data => {
                const items = data.results || [];
                setResults(items);
                setSearched(true);
                setStatus('success');
                setStatusText(`${items.length} result${items.length === 1 ? '' : 's'}`);
            })
            .catch(err => {
                setStatus('error');
                setStatusText(`Error: ${err.message}`);
                sendError({ demo: demoModule, action: 'search', error: err.message });
            });
    }, [query, kind, sourceKind]);

    return (
        <React.Fragment>
            <TopPanel label={demoModule} />
            <AppLayout
                navigation={<ServiceNavigation />}
                content={
                    <SpaceBetween direction="vertical" size="l">
                        <BrowserCheck />

                        <SynopsisPanelV3 label="Synopsis" demotype="tool" architectureImgs={[]}>
                            Semantic (vector) search across every image already indexed by{' '}
                            <strong>Blog Checker</strong> — console screenshots, architecture
                            diagrams, sequence diagrams, network diagrams, flow diagrams, charts,
                            code screenshots, and photos extracted from reviewed AWS blog posts
                            and whitepapers.
                            <br /><br />
                            Enter a plain-language description of what you're looking for — this
                            is a <strong>concept search</strong>, not a keyword match, so a query
                            like "diagram showing CloudFront forwarding requests to an S3 origin"
                            can match an image whose stored description never uses those exact
                            words. Each result links back to the blog or whitepaper it came from.
                            <br /><br />
                            💡 Query text is embedded with Amazon Titan Text Embeddings V2 and
                            matched via DynamoDB's native vector search (cosine similarity)
                            against the shared image index that Blog Checker populates. This
                            service never stores image bytes — results point to the image's
                            original source URL rather than a local copy.
                        </SynopsisPanelV3>

                        <Container>
                            <form onSubmit={handleSubmit}>
                                <Form actions={
                                    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                                        <StatusIndicator type={status}>{statusText}</StatusIndicator>
                                        <Button variant="primary">Search</Button>
                                    </SpaceBetween>
                                }>
                                    <SpaceBetween direction="vertical" size="s">
                                        <Alert type="error" visible={alertVisible} dismissible onDismiss={() => setAlertVisible(false)}>
                                            Enter a query before hitting 'Search'.
                                        </Alert>
                                        <ColumnLayout columns={3}>
                                            <FormField label="Search images" stretch>
                                                <Input
                                                    inputMode="search"
                                                    type="search"
                                                    value={query}
                                                    onChange={({ detail }) => setQuery(detail.value)}
                                                    placeholder="e.g. architecture diagram with CloudFront and S3"
                                                />
                                            </FormField>
                                            <FormField label="Kind (optional)">
                                                <Select
                                                    selectedOption={kind}
                                                    onChange={({ detail }) => setKind(detail.selectedOption)}
                                                    options={KIND_OPTIONS}
                                                />
                                            </FormField>
                                            <FormField label="Source (optional)">
                                                <Select
                                                    selectedOption={sourceKind}
                                                    onChange={({ detail }) => setSourceKind(detail.selectedOption)}
                                                    options={SOURCE_KIND_OPTIONS}
                                                />
                                            </FormField>
                                        </ColumnLayout>
                                    </SpaceBetween>
                                </Form>
                            </form>
                        </Container>

                        {searched && (
                            <Cards
                                cardDefinition={{
                                    header: (r) => (
                                        <Link external href={r.source_url}>
                                            {r.blog_title || r.source_url}
                                        </Link>
                                    ),
                                    sections: [
                                        { id: 'thumb', content: (r) => <Thumbnail result={r} onZoom={setZoomedResult} /> },
                                        {
                                            id: 'meta',
                                            content: (r) => (
                                                <SpaceBetween direction="horizontal" size="xs">
                                                    <Badge color={KIND_BADGE_COLOR[r.kind] || 'grey'}>
                                                        {KIND_LABEL[r.kind] || r.kind}
                                                    </Badge>
                                                    {r.source_kind && (
                                                        <Badge color="grey">
                                                            {SOURCE_KIND_LABEL[r.source_kind] || r.source_kind}
                                                        </Badge>
                                                    )}
                                                    <Box fontSize="body-s" color="text-body-secondary">
                                                        similarity: {typeof r.similarity === 'number' ? r.similarity.toFixed(3) : '—'}
                                                    </Box>
                                                </SpaceBetween>
                                            ),
                                        },
                                        {
                                            id: 'description',
                                            content: (r) => (
                                                <Box fontSize="body-s" color="text-body-secondary">
                                                    {r.description}
                                                </Box>
                                            ),
                                        },
                                    ],
                                }}
                                items={results}
                                cardsPerRow={[{ cards: 1 }, { minWidth: 600, cards: 2 }, { minWidth: 1000, cards: 3 }]}
                                trackBy="image_id"
                                empty={
                                    <Box textAlign="center" color="text-body-secondary">
                                        No matching images found. Try a broader or differently
                                        worded query, or clear the kind filter.
                                    </Box>
                                }
                            />
                        )}

                        <ExtendingPanel label="Extend it">
                            <ColumnLayout columns={2} variant='text-grid' className='extend_it'>
                                <Box>Questions/ideas on this demo?</Box>
                                <Box variant='strong'>Jaiganesh Girinathan, ganeshji@amazon.com</Box>
                            </ColumnLayout>
                        </ExtendingPanel>

                        <Modal
                            visible={!!zoomedResult}
                            onDismiss={() => setZoomedResult(null)}
                            size="large"
                            header={zoomedResult?.blog_title || zoomedResult?.source_url || 'Image preview'}
                        >
                            {zoomedResult && (
                                <SpaceBetween direction="vertical" size="s">
                                    <img
                                        src={zoomedResult.src}
                                        alt={zoomedResult.alt || zoomedResult.description || ''}
                                        style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', background: '#f2f3f3', borderRadius: 6 }}
                                    />
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <Badge color={KIND_BADGE_COLOR[zoomedResult.kind] || 'grey'}>
                                            {KIND_LABEL[zoomedResult.kind] || zoomedResult.kind}
                                        </Badge>
                                        {zoomedResult.source_kind && (
                                            <Badge color="grey">
                                                {SOURCE_KIND_LABEL[zoomedResult.source_kind] || zoomedResult.source_kind}
                                            </Badge>
                                        )}
                                    </SpaceBetween>
                                    <Box fontSize="body-s" color="text-body-secondary">
                                        {zoomedResult.description}
                                    </Box>
                                    <Link external href={zoomedResult.source_url}>
                                        {zoomedResult.blog_title || zoomedResult.source_url}
                                    </Link>
                                </SpaceBetween>
                            )}
                        </Modal>
                    </SpaceBetween>
                }
                contentType="default"
                navigationOpen={props?.navigationState}
                onNavigationChange={e => props?.setNavigationState?.(e.detail.open)}
            />
        </React.Fragment>
    );
}
